import _ from 'underscore';
import Promise from 'bluebird';
import {
  customColumnGridKeyFromId,
  getCustomColumnPreferenceName
} from '@bingads-webui-universal/custom-column-utils';
import { buildColumnAndSortingConversions } from '@bingads-webui-campaign/column-conversions';

import { Decorator } from './decorator';

/* eslint-disable no-param-reassign */
function getSelectQuery(
  savedColumnNames,
  lockedColumnNames,
  columnMap,
  IsAvgPositionDeprecationPhase2Enabled
) {
  if (IsAvgPositionDeprecationPhase2Enabled) {
    const hasAveragePosition = savedColumnNames.includes('AveragePosition');
    if (hasAveragePosition) {
      const hasTopImpressionRate = savedColumnNames.includes('TopImpressionRate');
      const hasAbsoluteTopImpressionRate = savedColumnNames.includes('AbsoluteTopImpressionRate');
      savedColumnNames = savedColumnNames.filter(column => column !== 'AveragePosition');
      if (!hasTopImpressionRate) {
        savedColumnNames.push('TopImpressionRate');
      }
      if (!hasAbsoluteTopImpressionRate) {
        savedColumnNames.push('AbsoluteTopImpressionRate');
      }
    }
  }

  const validNames = _
    .chain(lockedColumnNames)
    .concat(savedColumnNames)
    .filter(name => _.has(columnMap, name))
    .uniq()
    .value();

  return _.reduce(
    validNames,
    (selectedCols, name) => {
      const key = columnMap[name];

      if (_.isArray(key)) {
        Array.prototype.push.apply(selectedCols, key);
      } else if (_.isString(key)) {
        selectedCols.push(key);
      }
      return selectedCols;
    },
    []
  );
}

export class ColumnChooserDecorator extends Decorator {
  /**
   * Creates an instance of ColumnChooserDecorator.
   * @param {Object} {
   *     featureName - Name of feature to use for retriving from preference, ex: PerformanceGrid,
   *     columnsMap: Mapping of column names from preference to MT names,
   *     defaultColumns: Default list of columns to use if no preference found,
   *     lockedColumns: List of columns that should always be in query,
   *     customColumnsEnabled = false: Whether custom column feature is available or not,
   *     addPoPColumnsOnComparisonEnabled = false: Whether to add PoP columns when comparison is
   *      enabled or not,
   *     deps: The legacy dependencies of the decorator:
   *     deps.state: 'component/state/index' component
   *     deps.ColumnChooserPreference: from
   *      'component/bingads-grid/service/column-chooser-preferences',
   *     deps.CustomColumnPreference: from
   *     'component/bingads-grid/service/custom-column-preferences'
   *     deps.fetchSortInfo: from 'component/bingads-grid/service/fetch-sortinfo',
   *   }
   * @memberof ColumnChooserDecorator
   */
  constructor({
    featureName,
    columnsMap,
    defaultColumns,
    lockedColumns,
    customColumnsEnabled = false,
    IsAvgPositionDeprecationPhase2Enabled = false,
    IsColumnConversionEnabled = false,
    columnConversions,
    comparisonPoPColumns = [],
    deps,
  }) {
    super({
      name: 'columnChooser',
      deps,
      expectedDeps: [
        'state',
        'ColumnChooserPreference',
        'CustomColumnPreference',
        'fetchSortInfo',
      ],
    });

    this.state = deps.state;
    this.Columns = deps.ColumnChooserPreference.Columns;
    this.SelectedColumns = deps.ColumnChooserPreference.SelectedColumns;
    this.CustomColumns = deps.CustomColumnPreference.getCustomColumnPreferenceStore(featureName);

    this.featureName = featureName;
    this.columnsMap = columnsMap;
    this.defaultColumns = defaultColumns;
    this.lockedColumns = lockedColumns;
    this.customColumnsEnabled = customColumnsEnabled;
    this.IsAvgPositionDeprecationPhase2Enabled = IsAvgPositionDeprecationPhase2Enabled;
    this.comparisonPoPColumns = comparisonPoPColumns;
    this.fetchSortInfo = deps.fetchSortInfo;
    this.IsColumnConversionEnabled = IsColumnConversionEnabled;
    if (IsColumnConversionEnabled) {
      ({
        applyColumnConversions: this.applyColumnConversions,
        applySortingInfoConversion: this.applySortingInfoConversion,
      } = buildColumnAndSortingConversions(columnConversions));
    }
  }

  /**
   * @param {Object} customColumnsRecord preferences service response
   * @returns {Object} map of all custom columns
   */
  extractCustomColumnsInfo(customColumnsRecord) {
    if (!this.customColumnsEnabled) {
      return [{}, []];
    }

    const allCustomColumns =
      customColumnsRecord && _.isFunction(customColumnsRecord.get)
        ? customColumnsRecord.get('customColumns')
        : _.result(customColumnsRecord, 'customColumns', []);
    const customColumnsMap = {};

    _.each(allCustomColumns, (column) => {
      const name = customColumnGridKeyFromId(column.id);

      customColumnsMap[name] = name;
    });

    return customColumnsMap;
  }

  warmCache(preferenceKey) {
    const promises = [
      this.Columns.findAll({ where: preferenceKey }),
      this.SelectedColumns.findAll({ where: preferenceKey }),
    ];

    if (this.customColumnsEnabled) {
      promises.push(this.CustomColumns.findAll({
        where: {
          grid: getCustomColumnPreferenceName(preferenceKey.grid),
          project: preferenceKey.project,
        },
      }));
    }

    return Promise.all(promises);
  }

  getValueAsync() {
    const preferenceKey = {
      project: 'CampaignUI',
      grid: this.featureName,
    };

    const columnsPromise = this.warmCache(preferenceKey)
      .then(() =>
        _.first(this.SelectedColumns.filter({ where: preferenceKey })))
      .then((selection) => {
        const name = _.result(selection, 'name', 'Custom');
        const savedColumns = _.first(this.Columns.filter({
          where: _.defaults({ name }, preferenceKey),
        }));
        const customColumnsRecord =
          this.customColumnsEnabled === true
            ? _.first(this.CustomColumns.filter({
              where: {
                grid: getCustomColumnPreferenceName(preferenceKey.grid),
                project: preferenceKey.project,
              },
            }))
            : null;

        return { savedColumns, customColumnsRecord };
      });

    return Promise.all([
      columnsPromise,
      this.state.getPromise('$daterange'),
      this.fetchSortInfo(this.featureName),
    ]).then(([{ savedColumns, customColumnsRecord }, dateRange, sortInfo]) => ({
      savedColumns,
      customColumnsRecord,
      dateRange,
      orderbyInfo: sortInfo.orderbyInfo || {},
      sortableHeader: sortInfo.sortableHeader,
    }));
  }

  buildQuery(
    queryParams,
    {
      savedColumns: savedColumnsRecord, customColumnsRecord, dateRange, orderbyInfo, sortableHeader,
    }
  ) {
    let savedColumns = _.result(savedColumnsRecord, 'columns');

    if (this.IsColumnConversionEnabled && savedColumns) {
      savedColumns = this.applyColumnConversions(savedColumns);
    }

    const selectedColumns = savedColumns || this.defaultColumns;
    const customColumnsMap = this.extractCustomColumnsInfo(customColumnsRecord);

    const transformedQueryParams = _.extend({}, queryParams, {
      select: getSelectQuery(
        selectedColumns,
        this.lockedColumns,
        _.extend({}, this.columnsMap, customColumnsMap),
        this.IsAvgPositionDeprecationPhase2Enabled
      ),
    });

    if (
      !_.isEmpty(this.comparisonPoPColumns) &&
      dateRange.compared &&
      dateRange.compared.start
    ) {
      transformedQueryParams.select =
        transformedQueryParams.select.concat(this.comparisonPoPColumns);
    }

    transformedQueryParams.select = _.uniq(transformedQueryParams.select).sort();

    if (this.IsColumnConversionEnabled) {
      const { orderbyInfo: patchedOrderbyInfo } =
        this.applySortingInfoConversion({ sortableHeader, orderbyInfo });
      if (patchedOrderbyInfo && patchedOrderbyInfo.length > 0) {
        transformedQueryParams.orderby = patchedOrderbyInfo;
      }
    } else if (orderbyInfo && orderbyInfo.length > 0) {
      transformedQueryParams.orderby = orderbyInfo;
    }

    return transformedQueryParams;
  }
}
